/**
 * Pure gating decision for browser interval polls of server endpoints.
 *
 * A poll tick fetches only when ALL of these hold:
 *  - the poll is ACTIVE (the caller's own "is there anything to watch" gate);
 *  - the tab is not hidden (a hidden tab pauses the poll entirely);
 *  - no previous request is still in flight (no overlapping fetches), unless
 *    that request is older than `staleInFlightMs` — a hung request must not
 *    freeze the poll forever, so the next tick supersedes it;
 *  - the last fetch is not so recent that this tick would duplicate it.
 *
 * Becoming visible again triggers one catch-up fetch, but only when at least a
 * full interval has passed since the last fetch (i.e. the poll actually missed
 * a tick while hidden). Rapid tab switching therefore never fetches more often
 * than the poll itself would.
 */

export type PollTrigger = 'interval' | 'visible';

export type PollTickDecision =
  | 'fetch'
  | 'skip-inactive'
  | 'skip-hidden'
  | 'skip-in-flight'
  | 'skip-recent';

export interface PollTickInput {
  trigger: PollTrigger;
  /** The caller's activity gate. */
  active: boolean;
  /** `document.visibilityState === 'hidden'`. */
  hidden: boolean;
  /** Age (ms) of the request currently in flight, or null when none is. */
  inFlightForMs: number | null;
  /** Ms since the last fetch started (poll-driven or one-off). */
  msSinceLastFetch: number;
  intervalMs: number;
  /** An in-flight request at least this old no longer blocks a tick. */
  staleInFlightMs: number;
}

export function decidePollTick(input: PollTickInput): PollTickDecision {
  if (!input.active) return 'skip-inactive';
  if (input.hidden) return 'skip-hidden';
  if (input.inFlightForMs !== null && input.inFlightForMs < input.staleInFlightMs) {
    return 'skip-in-flight';
  }
  // Interval ticks: a fetch in the last half-interval (a one-off refetch or a
  // visibility catch-up) already covers this tick. Half, not a full interval,
  // so ordinary timer jitter can never suppress a regular tick.
  // Visibility catch-up: only when the poll missed at least one full interval.
  const recentWithinMs = input.trigger === 'visible'
    ? input.intervalMs
    : input.intervalMs / 2;
  if (input.msSinceLastFetch < recentWithinMs) return 'skip-recent';
  return 'fetch';
}

/** True only when a DOM document exists and reports itself hidden. */
export function isDocumentHidden(
  doc: Pick<Document, 'visibilityState'> | undefined = typeof document === 'undefined'
    ? undefined
    : document,
): boolean {
  return doc?.visibilityState === 'hidden';
}
